import { useState, forwardRef } from "react";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { Car } from "../../types/car.types";
import { validateForm } from "../../utils/carFormValidators";

interface CarDetailsCardProps {
  onSubmit: (carData: any) => void;
  onClose: () => void;
  carDetails?: Car;
}

export const CarDetailsCard = ({
  onSubmit,
  onClose,
  carDetails,
}: CarDetailsCardProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [carFormErrors, setCarFormErrors] = useState<Record<string, string>>(
    {}
  );
  const [data, setData] = useState<Car>(
    carDetails || {
      number: "",
      brand: "",
      model: "",
      carType: "",
      insuranceExpiry: null,
      fuelType: "",
      driveFrequency: "",
      lastServiceDate: null,
      kms: "",
      lastServiceKms: "",
      year: "",
    }
  );

  const previousStep = () => {
    setStep((prev) => prev - 1);
  };

  const nextStep = () => {
    const err = validateForm(step, data);
    setCarFormErrors(err);
    if (Object.keys(err).length === 0) setStep((prev) => prev + 1);
  };

  const handleFinalSubmit = () => {
    const err = validateForm(step, data);
    setCarFormErrors(err);
    if (Object.keys(err).length === 0) {
      onSubmit(data);
    }
  };

  const DateInput = forwardRef(({ value, onClick }: any, ref: any) => (
    <input
      type="text"
      value={value}
      onClick={onClick}
      ref={ref}
      className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700 cursor-pointer"
      placeholder="Select date"
      readOnly
    />
  ));
  DateInput.displayName = "DateInput";

  return (
    <div className="fixed animate-none inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="relative bg-popupLight dark:bg-popupDark text-primaryText dark:text-primaryTextDark shadow-lg rounded-2xl p-8 w-full max-w-lg mx-4 animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-2xl font-bold text-center text-primaryText dark:text-primaryTextDark mb-4">
          {step === 1
            ? "Car Details"
            : step === 2
            ? "Other Details"
            : "Usage Details"}
        </h2>
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium">
                {t("Car Number")}
              </label>
              <input
                type="text"
                value={data.number}
                onChange={(e) => setData({ ...data, number: e.target.value })}
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
                placeholder="KL01DE1234"
              />
              <span className="text-red-500 text-sm">
                {t(carFormErrors.number)}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium">{t("Brand")}</label>
              <input
                type="text"
                value={data.brand}
                onChange={(e) => setData({ ...data, brand: e.target.value })}
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
                placeholder="Hyundai, Maruti..."
              />
              <span className="text-red-500 text-sm">
                {carFormErrors.brand}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium">{t("Model")}</label>
              <input
                type="text"
                value={data.model}
                onChange={(e) => setData({ ...data, model: e.target.value })}
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
                placeholder="Creta, Baleno..."
              />
              <span className="text-red-500 text-sm">
                {carFormErrors.model}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium">
                {t("Car Type")}
              </label>
              <input
                type="text"
                value={data.carType}
                onChange={(e) => setData({ ...data, carType: e.target.value })}
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
                placeholder="suv, sedan, hatchback"
              />
              <span className="text-red-500 text-sm">
                {carFormErrors.carType}
              </span>
            </div>

            <button
              onClick={nextStep}
              className="w-full flex items-center justify-center gap-2 bg-secondaryText dark:bg-secondaryTextDark text-primaryText py-2 rounded-md hover:bg-blue-700 transition"
            >
              {t("Next")}
              <ArrowRight size={18} />
            </button>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium">
                {t("Dashboard KMs")}
              </label>
              <input
                type="text"
                value={data.kms}
                onChange={(e) => setData({ ...data, kms: e.target.value })}
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
                placeholder="12000"
              />
              <span className="text-red-500 text-sm">{carFormErrors.kms}</span>
            </div>

            <div>
              <label className="block text-sm font-medium">
                {t("Insurance Expiry Date")}
              </label>
              <DatePicker
                selected={
                  data.insuranceExpiry ? new Date(data.insuranceExpiry) : null
                }
                onChange={(date) =>
                  setData({
                    ...data,
                    insuranceExpiry: date ? date.toISOString() : null,
                  })
                }
                customInput={<DateInput />}
                popperPlacement="top-start"
              />
              <span className="text-red-500 text-sm">
                {carFormErrors.insuranceExpiry}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium">
                {t("Fuel Type")}
              </label>
              <select
                value={data.fuelType}
                onChange={(e) => setData({ ...data, fuelType: e.target.value })}
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
              >
                <option value="">{t("Select")}</option>
                <option value="petrol">{t("Petrol")}</option>
                <option value="diesel">{t("Diesel")}</option>
                <option value="electric">{t("Electric")}</option>
                <option value="cng">{t("CNG")}</option>
              </select>
              <span className="text-red-500 text-sm">
                {carFormErrors.fuelType}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium">{t("Year")}</label>
              <input
                type="text"
                value={data.year}
                onChange={(e) => {
                  const value = e.target.value;
                  setData({ ...data, year: value });
                }}
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
                placeholder="2020"
              />
              <span className="text-red-500 text-sm">{carFormErrors.year}</span>
            </div>

            <div className="flex justify-between pt-2">
              <button
                onClick={previousStep}
                className="flex items-center gap-2 px-4 py-2 rounded-md border dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowLeft size={18} />
                {t("Back")}
              </button>
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-4 py-2 bg-secondaryText dark:bg-secondaryTextDark text-primaryText rounded-md hover:bg-blue-700"
              >
                {t("Next")}
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium">
                {t("Last Service Date")}
              </label>
              <DatePicker
                selected={
                  data.lastServiceDate ? new Date(data.lastServiceDate) : null
                }
                onChange={(date) =>
                  setData({
                    ...data,
                    lastServiceDate: date ? date.toISOString() : null,
                  })
                }
                customInput={<DateInput />}
                popperPlacement="top-start"
              />
              <span className="text-red-500 text-sm">
                {carFormErrors.lastServiceDate}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium">
                {t("KMs at Last Service")}
              </label>
              <input
                type="text"
                value={data.lastServiceKms}
                onChange={(e) =>
                  setData({ ...data, lastServiceKms: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
                placeholder="11000"
              />
              <span className="text-red-500 text-sm">
                {carFormErrors.lastServiceKms}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium">
                {t("How often do you drive?")}
              </label>
              <select
                value={data.driveFrequency}
                onChange={(e) =>
                  setData({ ...data, driveFrequency: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 rounded-md shadow-sm dark:bg-gray-700"
              >
                <option value="">{t("Select")}</option>
                <option value="daily">{t("Daily")}</option>
                <option value="weekdays">{t("Weekdays")}</option>
                <option value="weekends">{t("Weekends")}</option>
                <option value="rarely">{t("Rarely")}</option>
              </select>
              <span className="text-red-500 text-sm">
                {carFormErrors.driveFrequency}
              </span>
            </div>

            <div className="flex justify-between pt-2">
              <button
                onClick={previousStep}
                className="flex items-center gap-2 px-4 py-2 rounded-md border dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ArrowLeft size={18} />
                {t("Back")}
              </button>
              <button
                onClick={handleFinalSubmit}
                className="flex items-center gap-2 px-4 py-2 bg-secondaryText dark:bg-secondaryTextDark text-primaryText rounded-md hover:bg-green-700"
              >
                <Check size={18} />
                {t("Save Car")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
